import { generateForm } from '../../form/Form';
import { fetchAttributeDefinitions, fetchProfile, updateProfile, fetchLocales, fetchTimezones } from '../../../apis';
import { get, List, Map } from 'immutable';
import { handleFormErrors } from '../../form/Form.helpers';
var PROFILE_INCLUDES = 'details,attributesMap,memberships,profileAttributesMap';

var dataSources = function dataSources() {
  return {
    locales: {
      fn: fetchLocales,
      params: [],
      transform: function transform(result) {
        return result.data.locales;
      }
    },
    timezones: {
      fn: fetchTimezones,
      params: [],
      transform: function transform(result) {
        return result.data.timezones;
      }
    },
    profile: {
      fn: fetchProfile,
      params: [{
        include: PROFILE_INCLUDES
      }],
      transform: function transform(result) {
        return result.profile;
      }
    },
    attributeDefinitions: {
      fn: fetchAttributeDefinitions,
      params: [{
        attributeType: 'userAttributeDefinitions'
      }],
      transform: function transform(result) {
        return result.attributeDefinitions;
      }
    },
    profileAttributeDefinitions: {
      fn: fetchAttributeDefinitions,
      params: [{
        attributeType: 'userProfileAttributeDefinitions'
      }],
      transform: function transform(result) {
        return result.attributeDefinitions;
      }
    }
  };
};

var handleSubmit = function handleSubmit() {
  return function (values) {
    var profile = values.toJS();
    return updateProfile({
      profile: profile
    }).then(handleFormErrors('user', 'There was an error saving your profile.'));
  };
};

var fields = function fields() {
  return function (_ref) {
    var profile = _ref.profile;
    return profile && [{
      name: 'username',
      label: 'Username',
      type: 'text',
      required: true,
      enabled: false,
      initialValue: get(profile, 'username') || ''
    }, {
      name: 'email',
      label: 'Email',
      type: 'text',
      initialValue: get(profile, 'email') || ''
    }, {
      name: 'displayName',
      label: 'Display Name',
      type: 'text',
      initialValue: get(profile, 'displayName') || ''
    }, {
      name: 'enabled',
      label: 'Enabled?',
      type: 'checkbox',
      enabled: function enabled(_ref2) {
        var profile = _ref2.profile;
        return get(profile, 'spaceAdmin', true);
      },
      initialValue: get(profile, 'enabled', true)
    }, {
      name: 'spaceAdmin',
      label: 'Space Admin?',
      type: 'checkbox',
      enabled: function enabled(_ref3) {
        var profile = _ref3.profile;
        return get(profile, 'spaceAdmin', true);
      },
      initialValue: get(profile, 'spaceAdmin', false)
    }, {
      name: 'password',
      label: 'Password',
      type: 'password',
      required: function required(_ref4) {
        var values = _ref4.values;
        return values.get('changePassword');
      },
      visible: function visible(_ref5) {
        var values = _ref5.values;
        return values.get('changePassword');
      },
      "transient": function transient(_ref6) {
        var values = _ref6.values;
        return !values.get('changePassword');
      }
    }, {
      name: 'passwordConfirmation',
      label: 'Password Confirmation',
      type: 'password',
      required: function required(_ref7) {
        var values = _ref7.values;
        return values.get('changePassword');
      },
      visible: function visible(_ref8) {
        var values = _ref8.values;
        return values.get('changePassword');
      },
      "transient": function transient(_ref9) {
        var values = _ref9.values;
        return !values.get('changePassword');
      },
      constraint: function constraint(_ref10) {
        var values = _ref10.values;
        return values.get('passwordConfirmation') === values.get('password');
      },
      constraintMessage: 'Password Confirmation does not match'
    }, {
      name: 'changePassword',
      label: 'Change Password',
      type: 'checkbox',
      visible: true,
      initialValue: false,
      "transient": true,
      onChange: function onChange(_ref11, _ref12) {
        var values = _ref11.values;
        var setValue = _ref12.setValue;

        if (values.get('password') !== '') {
          setValue('password', '');
        }

        if (values.get('passwordConfirmation') !== '') {
          setValue('passwordConfirmation', '');
        }
      }
    }, {
      name: 'allowedIps',
      label: 'Allowed IP Addresses',
      placeholder: '*',
      type: 'text',
      required: false,
      enabled: function enabled(_ref13) {
        var profile = _ref13.profile;
        return get(profile, 'spaceAdmin', true);
      },
      initialValue: get(profile, 'allowedIps') || ''
    }, {
      name: 'preferredLocale',
      label: 'Preferred Locale',
      type: 'text',
      options: function options(_ref14) {
        var locales = _ref14.locales;
        return locales ? locales.map(function (locale) {
          return Map({
            value: locale.get('code'),
            label: locale.get('name')
          });
        }) : List();
      },
      initialValue: get(profile, 'preferredLocale') || ''
    }, {
      name: 'timezone',
      label: 'Timezone',
      type: 'text',
      options: function options(_ref15) {
        var timezones = _ref15.timezones;
        return timezones ? timezones.map(function (timezone) {
          return Map({
            value: timezone.get('id'),
            label: "".concat(timezone.get('name'), " (").concat(timezone.get('id'), ")")
          });
        }) : List();
      },
      initialValue: get(profile, 'timezone') || ''
    }, {
      name: 'attributesMap',
      label: 'Attributes',
      type: 'attributes',
      required: false,
      enabled: function enabled(_ref16) {
        var profile = _ref16.profile;
        return get(profile, 'spaceAdmin', true);
      },
      options: function options(_ref17) {
        var attributeDefinitions = _ref17.attributeDefinitions;
        return attributeDefinitions;
      },
      initialValue: get(profile, 'attributesMap'),
      placeholder: 'There are no attributes configured'
    }, {
      name: 'profileAttributesMap',
      label: 'Profile Attributes',
      type: 'attributes',
      required: false,
      options: function options(_ref18) {
        var profileAttributeDefinitions = _ref18.profileAttributeDefinitions;
        return profileAttributeDefinitions;
      },
      initialValue: get(profile, 'profileAttributesMap'),
      placeholder: 'There are no profile attributes configured'
    }, {
      name: 'memberships',
      label: 'Teams',
      type: 'team-multi',
      required: false,
      placeholder: 'Select a team...',
      options: [],
      enabled: function enabled(_ref19) {
        var profile = _ref19.profile;
        return get(profile, 'spaceAdmin', true);
      },
      initialValue: get(profile, 'memberships', List()).map(function (m) {
        return m.get('team');
      }),
      serialize: function serialize(_ref20) {
        var values = _ref20.values;
        return values.get('memberships').map(function (team) {
          return Map({
            team: team
          });
        });
      }
    }];
  };
};

export var ProfileForm = generateForm({
  formOptions: [],
  dataSources: dataSources,
  fields: fields,
  handleSubmit: handleSubmit
});
ProfileForm.displayName = 'ProfileForm';